import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';

// ============ Types ============

export interface BaselineAssessment {
  id: string;
  product_id: string;
  assessment_type: 'baseline';
  is_required: boolean;
  show_on_signup: boolean;
  title: string;
  description?: string | null;
  questions: AssessmentQuestion[];
}

export interface AssessmentQuestion {
  id: string;
  assessment_id: string;
  field_type: string;
  label: string;
  order_index: number;
  required: boolean;
  field_name?: string;
  min_value?: number | null;
  max_value?: number | null;
  options?: any | null;
}

// ============ Queries ============

/**
 * Check if user has completed baseline assessment for a product
 * @param userId - User ID
 * @param productId - Product ID ('mindcotine', 'relief', etc.)
 * @returns true if baseline assessment is completed or not required
 */
export async function hasCompletedBaselineAssessment(
  userId: string,
  productId: string
): Promise<ApiResult<boolean>> {
  // Get baseline assessment for product via product_content junction
  const { data: productContent, error: pcError } = await supabase
    .from('product_content')
    .select('content_id')
    .eq('product_id', productId)
    .eq('content_type', 'assessment');

  if (pcError) return err(ApiError.fromSupabaseError(pcError));

  if (!productContent || productContent.length === 0) {
    // No assessments linked to this product = profile complete
    return ok(true);
  }

  // Get baseline assessment from linked assessments
  const assessmentIds = productContent.map(pc => pc.content_id);
  const { data: assessment, error: assessmentError } = await supabase
    .from('assessments')
    .select('id')
    .in('id', assessmentIds)
    .eq('assessment_type', 'baseline')
    .eq('is_required', true)
    .maybeSingle();

  if (assessmentError) return err(ApiError.fromSupabaseError(assessmentError));

  if (!assessment) {
    // No required baseline assessment = profile complete
    return ok(true);
  }

  // Check if user has responses to this assessment
  const { data: responses, error: responsesError } = await supabase
    .from('assessment_responses')
    .select('id')
    .eq('user_id', userId)
    .eq('assessment_id', assessment.id)
    .limit(1);

  if (responsesError) return err(ApiError.fromSupabaseError(responsesError));

  return ok(responses && responses.length > 0);
}

/**
 * Get baseline assessment for a product
 * @param productId - Product ID ('mindcotine', 'relief', etc.)
 * @returns Baseline assessment with questions or null
 */
export async function getBaselineAssessment(
  productId: string
): Promise<ApiResult<BaselineAssessment | null>> {
  // Get assessment IDs linked to product
  const { data: productContent, error: pcError } = await supabase
    .from('product_content')
    .select('content_id')
    .eq('product_id', productId)
    .eq('content_type', 'assessment');

  if (pcError) return err(ApiError.fromSupabaseError(pcError));

  if (!productContent || productContent.length === 0) {
    return ok(null);
  }

  const assessmentIds = productContent.map(pc => pc.content_id);

  // Get baseline assessment with questions
  const { data: assessment, error: assessmentError } = await supabase
    .from('assessments')
    .select('*, assessment_fields(*)')
    .in('id', assessmentIds)
    .eq('assessment_type', 'baseline')
    .eq('is_required', true)
    .maybeSingle();

  if (assessmentError) return err(ApiError.fromSupabaseError(assessmentError));

  if (!assessment) return ok(null);

  // Transform to BaselineAssessment type
  const baselineAssessment: BaselineAssessment = {
    id: assessment.id,
    product_id: productId,
    assessment_type: 'baseline',
    is_required: assessment.is_required || false,
    show_on_signup: assessment.show_on_signup || false,
    title: assessment.title,
    description: assessment.description,
    questions: (assessment.assessment_fields || []).map((field: any) => ({
      id: field.id,
      assessment_id: field.assessment_id,
      field_type: field.field_type,
      label: field.label,
      order_index: field.order_index,
      required: field.required || false,
      field_name: field.field_name,
      min_value: field.min_value,
      max_value: field.max_value,
      options: field.options,
    })).sort((a: any, b: any) => a.order_index - b.order_index),
  };

  return ok(baselineAssessment);
}

/**
 * Get user's baseline assessment responses for a product
 * Returns ALL responses dynamically mapped by auto-generated field_name slug
 *
 * DYNAMIC DESIGN:
 * - field_name is auto-generated from label for ALL fields (e.g., "Quit Date" → "quit_date")
 * - Returns whatever fields exist in the baseline assessment
 * - Admin can add/remove/change fields without code changes
 * - Components access fields they need (e.g., baselineData.quit_date)
 * - Missing fields return undefined (components handle gracefully)
 *
 * @param userId - User ID
 * @param productId - Product ID
 * @returns Object with field_name slugs as keys and responses as values
 */
export async function getBaselineAssessmentResponses(
  userId: string,
  productId: string
): Promise<ApiResult<Record<string, any>>> {
  // Get baseline assessment
  const assessmentResult = await getBaselineAssessment(productId);
  if (!assessmentResult.success || !assessmentResult.data) {
    return ok({});
  }

  const assessment = assessmentResult.data;

  // Get user's responses with field information
  const { data: responses, error } = await supabase
    .from('assessment_responses')
    .select('*, assessment_fields(field_name, field_type)')
    .eq('user_id', userId)
    .eq('assessment_id', assessment.id);

  if (error) return err(ApiError.fromSupabaseError(error));

  // Map responses by field name
  const responseMap: Record<string, any> = {};
  responses?.forEach((response: any) => {
    const fieldInfo = response.assessment_fields;
    if (fieldInfo && fieldInfo.field_name) {
      // Extract the actual response value from the JSONB response field
      responseMap[fieldInfo.field_name] = response.response;
    }
  });

  return ok(responseMap);
}
